/**
 * Get Relevant Excel Files Tool - Schema
 * 
 * Schema for discovering Excel files relevant to the current task.
 * Combines open instances, recent files, and presentation references.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// INPUT SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const GetRelevantExcelFilesParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    maxRecentFiles: z.number().int().min(1).max(50).optional().default(15)
        .describe('Maximum number of recent files to include (default: 15)'),
    
    includeOpen: z.boolean().optional().default(true)
        .describe('Include files currently open in Excel (default: true)'),
    
    includeRecent: z.boolean().optional().default(true)
        .describe('Include recently accessed files (default: true)'),
    
    includePresentationRefs: z.boolean().optional().default(true)
        .describe('Include files referenced in the current PowerPoint (default: true)'),
});

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Reference to a shape in PowerPoint that uses this Excel file
 */
export interface ExcelShapeReference {
    slideId: string;
    slideIndex: number;
    shapeId: string;
    shapeType: "table" | "chart";
    excelRange?: string;
    lastSync?: string;
}

/**
 * Information about a discovered Excel file
 */
export interface ExcelFileInfo {
    /** Full file path */
    path: string;
    
    /** Just the file name (for display) */
    fileName: string;
    
    /** Carousel workbook ID (if known) */
    workbookId: string | null;
    
    /** Currently open in Excel? */
    isOpen: boolean;
    
    /** Where this file was discovered from */
    source: "open_instance" | "recent" | "presentation_reference";
    
    /** Last accessed timestamp (ISO format) */
    lastAccessed: string | null;
    
    /** Last modified timestamp (ISO format) */
    lastModified: string | null;
    
    /** File size in KB */
    fileSizeKb: number | null;
    
    /** PowerPoint shapes that reference this file (if from presentation) */
    references: ExcelShapeReference[];
}

/**
 * Tool response with discovered files
 */
export interface GetRelevantExcelFilesResponse {
    /** List of discovered files, prioritized */
    files: ExcelFileInfo[];
    
    /** Total count */
    count: number;
    
    /** Breakdown by source */
    sources: {
        openCount: number;
        recentCount: number;
        presentationRefCount: number;
    };
    
    /** Pre-formatted summary for LLM */
    summary: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type GetRelevantExcelFilesParams = z.infer<typeof GetRelevantExcelFilesParamsSchema>;
